import getByPath from 'lodash/get';
import { createSelector } from 'reselect';
import { generateObjectId, joyDocHelper, tableHelper } from '@builttocreate/engine-utils';

import { getPageOrder } from '@builttocreate/engine-utils/dist/joyDocHelper';
import FileViews from '../constants/FileViews';
import FormStylesOptions from '../constants/FormStylesOptions';
import FieldPresentationModes from '../constants/FieldPresentationModes';
import FieldTypes from '../constants/FieldTypes';
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import PageDisplayTypes from '../constants/PageDisplayTypes';
import PageLayoutModes from '../constants/PageLayoutModes';
import FieldProperties from '../constants/FieldProperties';
import FieldPositionProperties from '../constants/FieldPositionProperties';
import ChangelogTypes from '../constants/ChangelogTypes';

const { getCleanedJoyDoc, duplicateDocumentPage } = joyDocHelper;

const { generateTableLookup, getColumns, sortColumns, getRows, getRowOrder } = tableHelper;

export const getDataPathPopulatedFields = (data, fields) => {

  if (!data) return fields;

  return fields.map((field) => {

    if (!field.dataPath) return field;

    const nextField = { ...field };
    const path = nextField.dataPath.split('.');

    const value = getByPath(data, path, field.value);

    nextField.value = value;

    return nextField;

  });

};

/**
 * Create default page
 *
 * @param {Object} defaults
 * @return {Object}
 */
export const getDefaultJoyDocPage = (defaults = {}) => {

  const nextPage = {
    _id: generateObjectId(),
    name: 'New Page',
    width: 816,
    height: 1056,
    rowHeight: 8,
    cols: 8,
    fieldPositions: [],
    layout: PageLayoutModes.grid,
    presentation: FieldPresentationModes.normal,
    padding: 24,
  };

  if (defaults.displayType === PageDisplayTypes.pdf) {
    nextPage.layout = PageLayoutModes.float;
    nextPage.presentation = FieldPresentationModes.transparent;
    nextPage.rowHeight = 1;
    nextPage.cols = 816;
    nextPage.padding = 0;
  }

  return {
    ...nextPage,
    ...defaults
  };

};

/**
 * Generate the mobile view and add it to the file.
 *
 * This method only adds a mobile view to the file if
 * one does not already exist.
 *
 * @param {Object} file
 * @returns {Object} //File with the mobile view added to `file.views` property
 */
export const generateAndPopulateMobileViewForFile = (file) => {

  const nextFile = { ...file, views: file.views ? [...file.views] : [] };
  const existingMobileView = nextFile.views.find((view) => view?.type === FileViews.mobile);

  /**
   * Step 1: If mobile view already exists return unmodified file.
   */
  if (existingMobileView) {

    if (existingMobileView && !existingMobileView.pageOrder) {
      existingMobileView.pageOrder = getPageOrder(null, existingMobileView.pages);
    }

    return nextFile;

  }

  /**
   * Step 2: Generate new mobile view from existing primary view. Primary view is the `file.pages` property.
   */
  nextFile.views.push(generateMobileViewForFile(nextFile));

  return nextFile;

};

/**
 * Generate the mobile view and add specific mobile view properties
 *
 * @param {Object} file //Full file object
 * @returns {Object}
 */
export const generateMobileViewForFile = (file) => {

  return {
    _id: generateObjectId(),
    type: FileViews.mobile,
    pages: [...file.pages].map((page) => {
      return formatPageForMobileView({ ...page });
    }),
    pageOrder: getPageOrder(null, file.pages)
  };

};

/**
 * Apply the proper page defaults for a mobile view to existing page.
 *
 * @param {Object} page
 * @returns {Objectg}
 */
export const formatPageForMobileView = (page) => {
  return {
    ...page,
    fieldPositions: page.fieldPositions ? getMobileViewFormattedPageFieldPositions(page.fieldPositions) : [],
    cols: 1,
    rowHeight: 1,
    padding: 12,
    width: 816,
    height: 1056,
    layout: PageLayoutModes.grid,
    presentation: FieldPresentationModes.normal,
    backgroundImage: '',
    fileName: '',
    filePath: ''
  };
};

export const getMobileViewFormattedPageFieldPositions = (fieldPositions) => {

  const blockFieldStyles = [
    'fontSize',
    'fontColor',
    'fontStyle',
    'fontWeight',
    'textAlign',
    'textTransform',
    'textDecoration',
    'textOverflow',
    'borderColor',
    'borderRadius',
    'borderWidth',
    'backgroundColor',
    'padding',
  ];

  const richTextFieldStyles = [
    'backgroundColor',
    'borderColor',
    'borderRadius',
    'borderWidth',
  ];

  const mobileFields = [];

  /**
   * Step 1: Sort fieldPositions
   *
   * For example:
   * [
   *   { "y": 3, "x": 4 },
   *   { "y": 3, "x": 1 },
   *   { "y": 2, "x": 0 },
   *   { "y": 1, "x": 5.22 },
   *   { "y": 1, "x": 10 },
   *   { "y": 1, "x": 0 },
   * ]
   * will become the following after sort
   * [
   *   { "y": 1, "x": 0 },
   *   { "y": 1, "x": 5.22 },
   *   { "y": 1, "x": 10 },
   *   { "y": 2, "x": 0 },
   *   { "y": 3, "x": 1 },
   *   { "y": 3, "x": 4 }
   * ];
   */
  const sortedFieldPositions = sortFieldPositionsByXAndYCoordinates(fieldPositions);

  sortedFieldPositions.forEach((joySpecFieldPosition) => {

    const mobileFieldData = {
      ...joySpecFieldPosition,
      _id: joySpecFieldPosition._id,
      type: joySpecFieldPosition.type,
      field: joySpecFieldPosition.field,
      displayType: FieldDisplayTypes.original,
      x: 0,
      y: joySpecFieldPosition.y,
      width: 1,
      height: joySpecFieldPosition.height,
      titleDisplay: FormStylesOptions.displayInline
    };

    /**
     * Step 1.1: Pull mobile field specific properties off of fieldPosition
     */
    Object.keys(joySpecFieldPosition).forEach((key) => {

      if (mobileFieldData.type === FieldTypes.block && blockFieldStyles.indexOf(key) !== -1) mobileFieldData[key] = joySpecFieldPosition[key];

      if (mobileFieldData.type === FieldTypes.richText && richTextFieldStyles.indexOf(key) !== -1) mobileFieldData[key] = joySpecFieldPosition[key];

    });

    /**
     * Step 1.2 Hide chart lines on mobile
     */
    if (joySpecFieldPosition.type === FieldTypes.chart) mobileFieldData.primaryDisplayOnly = true;

    /**
     * Step 1.3 disabled Image field should not trigger file uploads upon click on mobile
     */
    if (joySpecFieldPosition.type === FieldTypes.image && joySpecFieldPosition.primaryDisplayOnly) mobileFieldData.primaryDisplayOnly = joySpecFieldPosition.primaryDisplayOnly;

    mobileFields.push(getDefaultWidthAndHeightForField(mobileFieldData));

  });

  return filterDuplicatedFieldsGeneratedFromFieldPositionsForMobileView(mobileFields);

};

/**
 * Filter Duplicated Fields Generated From Field Positions
 *
 * Why do duplicate fields get created from fieldPositions
 *
 * The same field can get added twice because we may have multiple fieldPositions belonging
 * to the same field. This can happen with dropdown, multiSelect, and table fields.
 *
 * Since the fields get generated by looping over the fieldPositions and not regular fields
 * we may encounter the the same field multiple times because it has multiple fieldPositions.
 *
 * @param {Array} fields
 * @returns {Array}
 */
export const filterDuplicatedFieldsGeneratedFromFieldPositionsForMobileView = (fields) => {

  const filteredFields = [];

  fields.forEach((field) => {

    const fieldIndex = filteredFields.findIndex((filtered) => filtered.field.toString() === field.field.toString());

    //Only add field to the list if it hasn't already been added
    if (fieldIndex === -1) filteredFields.push(field);

  });

  return filteredFields;

};

/**
 * Add proper cellValue and caluclatedCellValue to individually mapped
 * table cell fields. This is important to make sure cell forumlas are
 * properly calculated and added.
 *
 * * cellValue represents the normal value: =A1+A2
 * * calculatedCellValue represents the regular of the formula calculation: 10
 */
export const getTableFormattedPageFields = createSelector(
  (state) => state.fields,
  (fieldPositions) => {

    /**
     * We only want to generate the table value calculated lookup for each table field once.
     * We use these lookups because multiple table fieldPositions belonging to the same table
     * field will all use the same table value calculated lookup.
     */
    const tableFieldLookups = {};
    const tableFieldColumnLookups = {};

    const nextFieldPositions = [];

    [...fieldPositions].forEach((fieldPosition) => {

      const nextFieldPosition = { ...fieldPosition };

      if (fieldPosition.type === FieldTypes.table) {

        const { field, column, rowIndex, tableColumns, value, rowOrder, tableColumnOrder } = fieldPosition;

        /**
         * Value will include deleted rows as well. We are removing the deleted rows
         * since we want to compute the cellValue and calculatedCellValue only
         * with undeleted table rows
         */
        const rows = getRows(value);
        const nextRowOrder = getRowOrder(rowOrder, value);

        if (!tableFieldColumnLookups[field]) {
          const fieldColumnLookup = {};
          getColumns(sortColumns(tableColumns, tableColumnOrder)).forEach((column) => fieldColumnLookup[column._id] = column);
          tableFieldColumnLookups[field] = fieldColumnLookup;
        }

        if (!tableFieldLookups[field]) {
          const fieldTableLookup = generateTableLookup(rows, sortColumns(tableColumns, tableColumnOrder));
          tableFieldLookups[field] = fieldTableLookup;
        }

        /**
         * Ensure each table field has a properly formatted rowOrder
         */
        if (!nextFieldPosition.rowOrder) nextFieldPosition.rowOrder = nextRowOrder;

        /**
         * Individual Table Cell vs Entire Table
         *
         * Only add cellValue, calculatedCellValue, columnType, and column options to
         * individually mapped table cell fieldPositions. Regular table fields (entire table)
         * just simply use the fieldPositions.value parameter so we don't need the calculated
         * values, etc.
         *
         * We determine an individual table cell fieldPosition vs an entire table
         * fieldPosition by checking for column and rowIndex. Individual cells have
         * column and rowIndex but regular tables do not.
         *
         * Note:
         * rowIndex + 1 is used while calculating cell value since column operands
         * start from 1 instead of zero in the table lookups
         * (ie: [A1:{}, B1:{}] instead of [A0:{}, B0:{}])
         *
         * If tableRowIndex associated with a cell doesn't have a table row associated
         * with it (ie rows[rowIndex] is undefined) it means that particular row is deleted
         */
        if (tableFieldLookups[field] && column && typeof rowIndex === 'number') {

          /**
           * What is the difference between rowIndex and targetRowIndex?
           *
           * - The rowIndex on the fieldPosition is the saved index of where the rowId lives inside of the rowOrder.
           * - The targetRowIndex is the actual index of the row object inside the value (array of row data) of the field.
           *
           * We use the fieldPosition.rowIndex to get the rowId from the rowOrder. Then we use the rowId to locate
           * the assoicated row object data in the value property. The rowOrder and value (rows) do not follow the same
           * order. The rowOrder is updated when users insert rows, move rows up or down, etc. it manages where rows
           * are at in the list and how they should be rendered. This is why the rowIndex uses the rowOrder.
           */
          const targetColumn = tableFieldColumnLookups[field][column];
          const targetRowId = nextRowOrder[rowIndex];
          const targetRowIndex = rows.findIndex((row) => row._id === targetRowId);

          /**
           * Add proper rowId to the field position so that it can be used
           * inside the element for change handlers;
           */
          nextFieldPosition.rowId = targetRowId;

          /**
           * If rows[rowIndex] is undefined, it means the row that was present
           * at the particular rowIndex was deleted
          */
          nextFieldPosition.cellValue = rows[targetRowIndex]?.cells[column] || '';

          //targetColumn will appear as undefined if a column is deleted
          if (targetColumn) {

            nextFieldPosition.calculatedCellValue = tableFieldLookups[field][`${targetColumn.operand}${targetRowIndex + 1}`];

            nextFieldPosition.columnType = targetColumn.type;

            nextFieldPosition.options = targetColumn.options;

            nextFieldPosition.columnIdentifier = targetColumn.identifier;

            //Push individual table cells if their associated column is not deleted
            nextFieldPositions.push(nextFieldPosition);

          }

        }
        else {
          //Push table fields with original display type
          nextFieldPositions.push(nextFieldPosition);
        }

      }
      else {
        //Push other fields
        nextFieldPositions.push(nextFieldPosition);
      }

    });

    return nextFieldPositions;

  }
);

/**
 * Selector for retrieving page fields
 *
 * Must pass the following values to this selector method:
 * * fieldPositions
 * * mode
 * * presentation
 *
 */
export const getPageFieldPositions = createSelector(
  getTableFormattedPageFields,
  (state) => state.fieldMeasurements,
  (fieldPositions, fieldHeightMeasurements) => {

    return fieldPositions.map((fieldPosition) => {

      const nextFieldPosition = { ...fieldPosition };

      if (fieldHeightMeasurements && fieldHeightMeasurements[fieldPosition._id]) {
        nextFieldPosition.height = fieldHeightMeasurements[fieldPosition._id].height;
      }

      return nextFieldPosition;

    });

  }
);

/**
 * Add position index to fields based on the y, x coordinate.
 *
 * This position index is used for things like setting the proper
 * z-index and tab index on fields.
 */
export const addPositionIndexesToPageFields = (pages) => {

  const nextPages = [...pages];
  const allPageFields = [];

  pages.forEach((page, pageIndex) => {

    if (!page.fields || page.fields.length < 0) return;

    const pageFields = sortFieldPositionsByXAndYCoordinates(page.fields);

    pageFields.forEach((field, fieldIndex) => {
      allPageFields.push({
        pageIndex,
        fieldIndex,
        field
      });
    });

  });

  allPageFields.forEach((fieldData, i) => {

    const { pageIndex, fieldIndex, field } = fieldData;

    const nextField = {
      ...field,
      zIndex: allPageFields.length - i
    };

    nextPages[pageIndex].fields = [...nextPages[pageIndex].fields];
    nextPages[pageIndex].fields[fieldIndex] = nextField;

  });

  return nextPages;

};

/**
 * Sort fields by their y and x coordinates
 *
 * @param {Array} fieldPositions
 * @returns {Array}
 */
export const sortFieldPositionsByXAndYCoordinates = (fieldPositions) => {

  const sortedFieldPositions = [...fieldPositions];

  sortedFieldPositions.sort((a, b) => {
    if (a.y === b.y) {
      return (a.x - b.x);
    } else if (a.y > b.y) {
      return 1;
    } else if (a.y < b.y) {
      return -1;
    } else {
      return 0;
    }
  });

  return sortedFieldPositions;

};

/**
 * Adjusts a number by a certain percentage difference
 *
 * Specifies the number that should be adjusted
 * @param {Number} value
 *
 * Specifies the percentage to adjust the number by.
 * Examples, 0.2 represents 20%, -0.1 represents negative 10%, etc.
 * @param {Number} difference
 *
 * @return {Number}
 */
export const getPercentageAdjustedValue = (value, difference) => {
  const original = (value * difference) + value;
  return Number((original).toFixed(2));
};

/**
 * Adjusts a number back to it's original value before it was adjusted by
 * the target percentage difference.
 *
 * For calculations see: https://www.tutorialspoint.com/percentage_increase_and_decrease/finding_original_amount_given_result_percentage_increase_or_decrease.htm
 *
 * Specifies the number that has been adjusted.
 * @param {Number} value
 *
 * Specifies the percentage that was used to adjust the original value.
 * Examples, 0.2 represents 20%, -0.1 represents negative 10%, etc.
 * @param {Number} difference
 *
 * @return {Number}
 */

export const reversePercentageAdjustedValue = (value, difference) => {
  let original = value;

  /**
   * Handle negative percentage change
   */
  if (Math.sign(difference) === -1) {
    original = value / (1 - Math.abs(difference));
  }
  /**
   * Handle positive percentage change
   */
  else if (difference !== 0) {
    original = value / (1 + difference);
  }

  return Number((original).toFixed(2));
};

/**
 * Calculates the percentage adjustment that needs page background image and fieldPositions
 * need to be scaled up or down by in order for the page to fit within the max width and
 * height dimensions of the containing element of the SDK.
 *
 * Why do we choose only one of the percentages generated from the width or height for determining
 * how to scale the page?  We must use the same percentage in order to maintain proper ratios and
 * not cause distortion or stretching. This is primarily used with PDF Forms.
 *
 * We always return the smallest number using Math.min for the following scaling scenarios:
 * * Scaling up - width 0.2 (20%), height 0.1 (10%) in this scenario it means that you can scale up
 *   width 20% and height 10% more to fit available container space. But as mentioned previously
 *   you cannot use two different percentages. We must use the smaller 10% and not the 20% because if
 *   you tried to scale the height by 20% it would overflow the container boundary. We chose 10% because
 *   both height and width can scale by that percentage without overflowing the boundary.
 * * Scaling down - width -0.2 (-20%), height -0.1 (-10%) in this scenario it means that the page width
 *   and height need to be scaled down in order to fit within the containing element. We again choose the
 *   smaller number (-2.0), -0.2 is a smaller number than -0.1 because it's more negative. We must use the
 *   more negative -20% number because only that will ensure that both height and width are scaled down
 *   enough to both fit within the container element boundary.
 *
 * @param {Number} pageHeight //Actual saved page.height
 * @param {Number} pageWidth //Actual saved page.width
 * @param {Number} maxHeight //Containing element width in pixels
 * @param {Number} maxWidth //Containing element height in pixels
 * @returns {Number} adjustmentPercentage - The percentage adjustement that should be applied to page width, height, background, fields, etc.
 *
 */
export const calculatePagePercentageAdjustments = (pageHeight, pageWidth, maxHeight, maxWidth) => {

  /**
   * Calculate what percentage adjustement is required to fix within
   * the max dimensions.
   */
  const widthAdjustmentPercent = ((maxWidth - pageWidth) / pageWidth);
  const heightAdjustmentPercent = ((maxHeight - pageHeight) / pageHeight);

  let adjustementPercentage = 0;

  /**
   * If the precents are negative numbers (less than 0) that means
   * the page needs to be scaled down that amount in order to
   * fit within the boundaries.
   */
  if (widthAdjustmentPercent !== 0 || heightAdjustmentPercent !== 0) {

    /**
     * Why Math.min? We need to adjust the page by the most negative number.
     * For instance, if we have a -5 and -2 we need to adust the page by -5
     * and that is what Math.min will return since -5 is a smaller number than -2.
     */
    adjustementPercentage = Math.min(widthAdjustmentPercent, heightAdjustmentPercent);

  }

  return adjustementPercentage;

};

/**
 * This is used to prevent fields being dragged or resized off the page.
 *
 * @param {Object} fieldData
 * @param {Object} parentBoundingClientRect //Comes from calling reactElement.getBoundingClientRect();
 * @param {Object} pageStyles
 * @returns {Boolean}
 */
export const getValidAdjustedPageDropPosition = (fieldData, parentBoundingClientRect) => {

  /**
  * Parent right and bottom bounding cooridinates.
  *
  * We don't have to worry about top or left because
  * the drag and drop zone doesn't detect a drop if
  * the cursor isn't in a valid position for left and top
  * drop actions.
  */
  const parentPositionRightMax = parentBoundingClientRect.width;
  const parentPositionBottomMax = parentBoundingClientRect.height;

  /**
   * Adjust the x & y coordinates of the drop position to start at
   * the x & y (top left) position of parent element and not viewport (top left).
   */
  const nextFieldData = { ...fieldData };

  nextFieldData.x -= parentBoundingClientRect.x;
  nextFieldData.y -= parentBoundingClientRect.y;

  /**
   * Handle right and bottom adjustments to place
   * the new field properly inside the parent rect.
   */
  const totalFieldXRequirements = nextFieldData.x + nextFieldData.width;
  const totalFieldYRequirements = nextFieldData.y + nextFieldData.height;

  if (totalFieldXRequirements > parentPositionRightMax) {
    nextFieldData.x = parentPositionRightMax - (nextFieldData.width + 8);
  }

  if (totalFieldYRequirements > parentPositionBottomMax) {
    nextFieldData.y = parentPositionBottomMax - (nextFieldData.height + 8);
  }

  /**
   * X and Y cannot have any value less than 0
   */
  if (nextFieldData.x < 0) nextFieldData.x = 0;
  if (nextFieldData.y < 0) nextFieldData.y = 0;

  return nextFieldData;

};

/*
 * Get default width and height based on field types.
 *
 * Targets:
 * Page 816px x 1056px
 * Page Settings of 816 columns and rowHeight of 1px.
 */
export const getDefaultWidthAndHeightForField = (field) => {

  const { type, displayType } = field;

  if (type === FieldTypes.image) {
    field.width = 204;
    field.height = 180;
  }
  else if (type === FieldTypes.file) {
    field.width = 350;
    field.height = 180;
  }
  else if (type === FieldTypes.richText) {
    field.width = 204;
    field.height = 180;
  }
  else if (
    (
      type === FieldTypes.text
      || type === FieldTypes.number
      || type === FieldTypes.date
      || type === FieldTypes.dropdown
    )
    && (displayType === FieldDisplayTypes.original || !displayType)
  ) {
    field.width = 408;
    field.height = 64;
  }
  else if (
    type === FieldTypes.multiSelect
    && (displayType === FieldDisplayTypes.original || !displayType)
  ) {
    field.width = 408;
    field.height = 120;
  }
  else if (type === FieldTypes.block) {
    field.width = 408;
    field.height = 40;
  }
  else if (type === FieldTypes.textarea) {
    field.width = 408;
    field.height = 180;
  }
  else if (
    (type === FieldTypes.table && (displayType === FieldDisplayTypes.original || !displayType))
    || (type === FieldTypes.inputGroup && (displayType === FieldDisplayTypes.original || !displayType))
  ) {
    field.width = 816;
    field.height = 116;
  }
  else if (type === FieldTypes.table && displayType === FieldDisplayTypes.text) {
    field.width = 200;
    field.height = 40;
  }
  else if (
    (type === FieldTypes.dropdown && displayType !== FieldDisplayTypes.original)
    || (type === FieldTypes.multiSelect && displayType !== FieldDisplayTypes.original)
    || (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.text)
  ) {
    field.width = 34;
    field.height = 34;
  }
  else if (type === FieldTypes.chart) {
    field.width = 408;
    field.height = 216;
  }
  else if (type === FieldTypes.signature) {
    field.width = 408;
    field.height = 180;
  }

  return field;
};

/**
 * Recalculate field position coordinates (width, height, x, y)
 * for the new columns and rowHeight values to match previous
 * equivalent dimensions.
 *
 * This is used so that even though you change the cols or rowHeight
 * the form fields still maintain their same position on the form.
 *
 * @param {Array} fields
 * @param {Number} newPageCols
 * @param {Number} newPageRowHeight
 * @param {Number} previousPageCols
 * @param {Number} previousPageRowHeight
 * @return {Array} //Array of page fields
 *
 */
export const recalculatePositionCoordinatesForPageFields = (
  fields,
  newPageCols,
  newPageRowHeight,
  previousPageCols,
  previousPageRowHeight
) => {

  if (!fields || fields.length < 1) return fields;

  return fields.map((field) => {
    return recalculatePositionCoordinatesForField(
      field,
      newPageCols,
      newPageRowHeight,
      previousPageCols,
      previousPageRowHeight
    );
  });

};

/**
 * Recalculate field position coordinates (width, height, x, y)
 * for the new columns and rowHeight values to match previous
 * equivalent dimensions.
 *
 * This is used so that even though you change the cols or rowHeight
 * the form fields still maintain their same position on the form.
 *
 * @param {Object} field
 * @param {Number} newPageCols
 * @param {Number} newPageRowHeight
 * @param {Number} previousPageCols
 * @param {Number} previousPageRowHeight
 * @return {Object}
 */
export const recalculatePositionCoordinatesForField = (
  field,
  newPageCols,
  newPageRowHeight,
  previousPageCols,
  previousPageRowHeight
) => {

  const nextField = { _id: field._id };

  /**
   * Re-calculate columns based on percentage difference
   */
  if (newPageCols && previousPageCols) {

    const colsDifference = (newPageCols - previousPageCols) / previousPageCols;

    nextField.x = getPercentageAdjustedValue(field.x, colsDifference);
    nextField.width = getPercentageAdjustedValue(field.width, colsDifference);

  }

  /**
   * Re-calculate rows based on percentage difference
   */
  if (newPageRowHeight && previousPageRowHeight) {

    const previousHeight = previousPageRowHeight * field.height; //field.height represents the number of rows
    const newHeight = newPageRowHeight * field.height;
    const rowHeightDifference = (previousHeight - newHeight) / newHeight;

    nextField.height = getPercentageAdjustedValue(field.height, rowHeightDifference);
    nextField.y = getPercentageAdjustedValue(field.y, rowHeightDifference);

  }

  return nextField;

};

/**
 * Separate propertes for field and fieldPosition and return objects
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const separateFieldAndFieldPositionChanges = (payload) => {

  /**
   * Why do I set the initla value of field and fieldPosition to false?
   *
   * I do this so that when we are evaluating if field or fieldPosition
   * should be updated in the object we can easily tell what object
   * the changed properties aply to in an if statement.
   */
  const changeObject = { fieldChanges: false, fieldPositionChanges: false };

  /**
   * Pull field specific properties off of fieldPosition
   */
  Object.keys(payload).forEach((key) => {

    if (FieldProperties.indexOf(key) !== -1) {

      if (!changeObject.fieldChanges) changeObject.fieldChanges = {};

      changeObject.fieldChanges[key] = payload[key];
    } else if (FieldPositionProperties.indexOf(key) !== -1) {

      if (!changeObject.fieldPositionChanges) changeObject.fieldPositionChanges = {};

      changeObject.fieldPositionChanges[key] = payload[key];
    }
  });

  return changeObject;

};

/**
 * Separate propertes for field and fieldPosition and return objects
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const getFieldAndFieldPositionFromCombinedPayload = (payload) => {

  const field = { _id: payload.field };
  const fieldPosition = { _id: payload._id };

  /**
   * Pull field specific properties off of fieldPosition
   */
  Object.keys(payload).forEach((key) => {

    //Type gets added to both field and fieldPosition
    if (key === 'type') {

      field[key] = payload[key];

      fieldPosition[key] = payload[key];

    } else if (FieldProperties.indexOf(key) !== -1) {

      field[key] = payload[key];

    } else if (FieldPositionProperties.indexOf(key) !== -1) {

      fieldPosition[key] = payload[key];

    }

  });

  return { field, fieldPosition };

};

export const getDuplicatedPageAndChangelogsForDocument = (doc, fileId, pageId) => {

  const result = duplicateDocumentPage(doc, fileId, pageId);

  const nextDoc = result.doc;

  const changelogs = [];

  const { primaryPages, viewPages, fields } = result.changelogInfo;

  if (primaryPages) {
    changelogs.push({
      sdk: 'js',
      v: 1,
      target: ChangelogTypes.pageCreate,
      _id: doc._id,
      identifier: doc.identifier,
      fileId,
      change: {
        page: primaryPages.page,
        targetIndex: primaryPages.targetIndex,
      },
      createdOn: new Date().getTime()
    });
  }

  if (viewPages && viewPages.length > 0) {
    viewPages.forEach(({ page, targetIndex, view, viewId }) => {
      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.pageCreate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        view,
        viewId,
        change: {
          page,
          targetIndex,
        },
        createdOn: new Date().getTime()
      });
    });
  }

  if (fields && fields.length > 0) {
    fields.forEach((newField) => {
      changelogs.push({
        sdk: 'js',
        v: 1,
        target: ChangelogTypes.fieldCreate,
        _id: doc._id,
        identifier: doc.identifier,
        fileId,
        change: newField,
        createdOn: new Date().getTime()
      });
    });
  }

  return { doc: nextDoc, changelogs };

};

/**
 * Get Clean JoyDoc Format
 *
 * Solves Issue: https://app.zenhub.com/workspaces/joyfill-platform-5f07262b507391001f56c7f9/issues/gh/zionlabs/issues/2105
 *
 * This selector ensures a couple of things:
 * 1. Valid file inside the document.
 * 2. Valid page inside document file.
 * 3. All _id's (page, file, page, field) are in a valid format
 * 4. Row order of table field is free from deleted/duplicated rowIds
 *
 * @param {Object} doc
 * @returns {Object}
 */
export const getCleanedDocSelector = createSelector(
  (state) => state.doc,
  getCleanedJoyDoc
);
